import React, { useState } from "react";
import {
    Box,
    Spinner,
} from "@chakra-ui/react";
import { FaSearch, FaUser, FaEnvelope, FaTrash, FaArrowLeft, FaLayerGroup } from "react-icons/fa";
import { IconWrapper } from "../../components/IconWrapper";
import { getThemeClasses } from "../../utils/themeUtils";
import { useColorMode } from "@chakra-ui/react";
import CustomDropdown from "../../components/CustomDropdown";
import { showToast } from "../../utils/toastUtils";

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    permissions?: "Admin" | "Editor" | "Viewer" | "";
    approvalStatus?: "Yes" | "No";
    workGroupRoleId?: string;
    [key: string]: any;
}

interface Role {
    id: string;
    name: string;
    permission: "Admin" | "Editor" | "Viewer";
    approvalStatus?: "Yes" | "No";
    createdAt?: any;
    createdBy?: string;
}

interface WorkGroupProps {
    users: User[];
    roles: Role[];
    onUpdatePermission: (userId: string, newPermission: string) => void;
    onAddRole: (name: string, permission: string, approvalStatus: string) => Promise<void>;
    onDeleteRole: (roleId: string) => Promise<void>;
    onAssignRole: (userId: string, roleId: string) => Promise<void>;
    onUpdateRole: (roleId: string, newName: string, newPermission: string, approvalStatus?: string) => Promise<void>;
    onUpdateUserApprovalStatus: (userId: string, approvalStatus: string) => Promise<void>;
    isLoading?: boolean;
    cardBg: string;
    textColor: string;
    currentUserRole?: string;
    currentUserPermissions?: string;
}

type ViewMode = "users" | "roles";

const WorkGroup: React.FC<WorkGroupProps> = ({
    users,
    roles,
    onUpdatePermission,
    onAddRole,
    onDeleteRole,
    onAssignRole,
    onUpdateRole,
    onUpdateUserApprovalStatus,
    isLoading = false,
    cardBg,
    textColor,
    currentUserRole,
    currentUserPermissions,
}) => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";
    const theme = getThemeClasses(isDark);
    const [viewMode, setViewMode] = useState<ViewMode>("users");

    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");


    const [newRoleName, setNewRoleName] = useState("");
    const [newRolePermission, setNewRolePermission] = useState<string>("Viewer");
    const [newRoleApprovalStatus, setNewRoleApprovalStatus] = useState<string>("");
    const [isAddingRole, setIsAddingRole] = useState(false);


    const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null);

    const roleLower = currentUserRole?.toLowerCase().trim() || "";
    const isRestrictedRole =
        roleLower !== "admin" && roleLower !== "manager" && roleLower !== "managing director";

    const isAdminLevel =
        roleLower === "admin" || roleLower === "manager" || roleLower === "managing director" ||
        currentUserPermissions?.toLowerCase().trim() === "admin";

    const filteredUsers = users.filter((user) =>
        `${user.firstName} ${user.lastName} ${user.email}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
    );



    const handlePermissionChange = async (userId: string, newValue: string) => {
        setUpdatingId(userId);
        await onUpdatePermission(userId, newValue);
        setUpdatingId(null);
    };

    const handleAssignRole = async (userId: string, roleId: string) => {
        setUpdatingId(userId);
        await onAssignRole(userId, roleId);
        setUpdatingId(null);
    };

    const handleAddRoleSubmit = async () => {
        if (!newRoleName.trim()) {
            showToast({ title: "Validation Error", description: "Role name is required.", status: "warning", duration: 3000, isClosable: true });
            return;
        }
        if (!newRoleApprovalStatus) {
            showToast({ title: "Validation Error", description: "Please select an approval status.", status: "warning", duration: 3000, isClosable: true });
            return;
        }
        setIsAddingRole(true);
        await onAddRole(newRoleName.trim(), newRolePermission, newRoleApprovalStatus);
        setNewRoleName("");
        setNewRolePermission("Viewer");
        setNewRoleApprovalStatus("");
        setIsAddingRole(false);
    };

    const handleDeleteRole = async (roleId: string) => {
        const assigned = users.find((u: any) => u.workGroupRoleId === roleId);
        if (assigned) {
            showToast({ title: "Cannot Delete", description: "Unassign this role from all users first.", status: "warning", duration: 3000, isClosable: true });
            return;
        }
        await onDeleteRole(roleId);
    };

    const handleRolePermissionToggle = async (roleId: string, permission: string) => {
        const role = roles.find((r) => r.id === roleId);
        if (!role || role.permission === permission) return;
        setUpdatingRoleId(roleId);
        await onUpdateRole(roleId, role.name, permission);
        setUpdatingRoleId(null);
    };

    const handleRoleApprovalStatusToggle = async (roleId: string, approvalStatus: string) => {
        const role = roles.find((r) => r.id === roleId);
        if (!role || role.approvalStatus === approvalStatus) return;
        setUpdatingRoleId(roleId);
        await onUpdateRole(roleId, role.name, role.permission, approvalStatus);
        setUpdatingRoleId(null);
    };

    const handleUserApprovalStatusChange = async (userId: string, approvalStatus: string) => {
        setUpdatingId(userId);
        await onUpdateUserApprovalStatus(userId, approvalStatus);
        setUpdatingId(null);
    };

    const getRoleForUser = (user: User): Role | undefined => {
        return roles.find((r) => r.id === (user as any).workGroupRoleId);
    };

    const roleDropdownOptions = [
        { value: "", label: "None" },
        ...roles.map((role) => ({ value: role.id, label: role.name })),
    ];

    return (
        <Box>
            {/* Header */}
            <div className={`relative flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}>
                {isDark && (
                    <>
                        <span className={theme.headerOverlayGlow} />
                        <span className={theme.headerOverlayTopLine} />
                        <span className={theme.headerOverlayBottomGlow} />
                        <span className={theme.headerOverlaySweep} />
                    </>
                )}
                <div className="flex flex-col space-y-1 relative z-10">
                    <span className={`text-2xl font-bold ${theme.text}`}>
                        {viewMode === "users" ? "Work Group Configuration" : "Role Management"}
                    </span>
                    <span className={`text-xs ${theme.textMuted}`}>
                        {viewMode === "users"
                            ? "Manage user permissions and roles within the work group"
                            : "Manage roles and their permission levels"}
                    </span>
                </div>
                <div className="relative z-10 flex items-center gap-2">
                    {viewMode === "roles" && (
                        <button
                            onClick={() => setViewMode("users")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 border ${isDark
                                ? 'border-[#B59143] text-white hover:bg-[#2A3441]'
                                : 'border-[#B59143] text-gray-700 hover:bg-[#FFF7E6]'
                                }`}
                        >
                            <IconWrapper icon={FaArrowLeft} size={14} />
                            Back to Users
                        </button>
                    )}
                    {viewMode === "users" && isAdminLevel && (
                        <button
                            onClick={() => setViewMode("roles")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${isDark
                                ? 'bg-[#B59143] text-black hover:bg-[#E8C16C]'
                                : 'bg-[#B59143] text-white hover:bg-[#9A7A38]'
                                }`}
                        >
                            <IconWrapper icon={FaLayerGroup} size={14} />
                            Manage Roles
                        </button>
                    )}
                </div>
            </div>

            {/* ===================== USERS VIEW ===================== */}
            {viewMode === "users" && (
                <div className={`w-full rounded-md ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'bg-white border border-gray-200 shadow-sm'}`}>
                    <div className={`flex justify-between items-center px-2 py-3 ${theme.card} border-b ${theme.border} flex-wrap rounded-t-md`}>
                        <div className={`relative flex items-center max-w-sm w-full rounded-full ${theme.cardMuted} overflow-hidden`}>
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <IconWrapper icon={FaSearch} className={`h-4 w-4 ${theme.textMuted}`} size={16} />
                            </div>
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={`${theme.searchInput} block w-full pl-10 pr-3 py-1.5 border rounded-full leading-5 focus:outline-none sm:text-sm`}
                            />
                        </div>

                    </div>
                    {isLoading && (
                        <div className={`absolute inset-0 flex items-center justify-center z-10 ${isDark ? 'bg-[#0B0B0F]/80' : 'bg-white/80'} backdrop-blur-sm`}>
                            <svg className="animate-spin h-10 w-10 text-[#B59143]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    )}
                    <div className="px-5 overflow-auto" style={{ maxHeight: "68vh" }}>
                        {!isLoading && filteredUsers.length === 0 ? (
                            <div className={`text-center py-8 col-span-full text-lg font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>
                                No users found.
                            </div>
                        ) : (
                            <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                                <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                                    <tr>
                                        <th scope="col" className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>S.No</th>
                                        <th scope="col" className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>Name</th>
                                        <th scope="col" className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>Email</th>
                                        <th scope="col" className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>Assigned Role</th>
                                        <th scope="col" className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>Permissions</th>
                                        <th scope="col" className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>Approval</th>
                                        <th scope="col" className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>Assign Role</th>
                                    </tr>
                                </thead>
                                <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                                    {filteredUsers.map((user, index) => {
                                        const assignedRole = getRoleForUser(user);
                                        return (
                                            <tr key={user.id} className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#F2F4F7]'}`}>
                                                <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    {index + 1}
                                                </td>
                                                <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    <div className="flex items-center">
                                                        <IconWrapper icon={FaUser} className="text-gray-400 mr-2" size={12} />
                                                        <span>{`${user.firstName} ${user.lastName}`}</span>
                                                    </div>
                                                </td>
                                                <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    <div className="flex items-center">
                                                        <IconWrapper icon={FaEnvelope} className="text-gray-400 mr-2" size={12} />
                                                        <span>{user.email}</span>
                                                    </div>
                                                </td>
                                                <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    {assignedRole ? (
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isDark ? 'border border-[#B59143] text-[#FBD282]' : 'bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C]'}`}>
                                                            {assignedRole.name}
                                                        </span>
                                                    ) : (
                                                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                                                    )}
                                                </td>
                                                <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    {assignedRole ? (
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isDark ? 'border border-[#4A90D9] text-[#7AB3EF]' : 'bg-[#E6F0FF] text-[#4A90D9] border border-[#7AB3EF]'}`}>
                                                            {assignedRole.permission}
                                                        </span>
                                                    ) : user.permissions ? (
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isDark ? 'border border-[#4A90D9] text-[#7AB3EF]' : 'bg-[#E6F0FF] text-[#4A90D9] border border-[#7AB3EF]'}`}>
                                                            {user.permissions}
                                                        </span>
                                                    ) : (
                                                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                                                    )}
                                                </td>
                                                <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    {assignedRole ? (
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${assignedRole.approvalStatus === "Yes"
                                                            ? isDark ? 'border border-[#48BB78] text-[#68D391]' : 'bg-[#F0FFF4] text-[#48BB78] border border-[#68D391]'
                                                            : isDark ? 'border border-[#FC8181] text-[#FEB2B2]' : 'bg-[#FFF5F5] text-[#FC8181] border border-[#FEB2B2]'
                                                            }`}>
                                                            {assignedRole.approvalStatus || "No"}
                                                        </span>
                                                    ) : (
                                                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                                                    )}
                                                </td>
                                                <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    <div className="relative inline-block">
                                                        <CustomDropdown
                                                            value={(user as any).workGroupRoleId || ""}
                                                            onChange={(value) => handleAssignRole(user.id, value)}
                                                            isDark={isDark}
                                                            placeholder="Select"
                                                            style={{ width: '170px' }}
                                                            options={roleDropdownOptions}
                                                        />
                                                        {updatingId === user.id && (
                                                            <div className={`absolute inset-0 flex items-center justify-center rounded ${isDark ? 'bg-[#1A1C1F]/70' : 'bg-white/70'}`}>
                                                                <Spinner size="xs" color="zs.accent" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                </div>
            )}

            {/* ===================== ROLES VIEW ===================== */}
            {viewMode === "roles" && (
                <div className="space-y-4">
                    {/* Roles List Card */}
                    <div className={`rounded-md overflow-hidden ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'bg-white border border-gray-200 shadow-sm'}`}>
                        {roles.length === 0 ? (
                            <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                No roles created yet. Add your first role below.
                            </div>
                        ) : (
                            <>
                                <div className="overflow-auto" style={{ maxHeight: "60vh" }}>
                                    <table className={`w-full`}>
                                        <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-100'}`}>
                                            <tr className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-100'}`}>
                                                <th scope="col" className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                                    Role Name
                                                </th>
                                                <th scope="col" className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                                    Access
                                                </th>
                                                <th scope="col" className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                                    Approval Status
                                                </th>
                                                <th scope="col" className={`px-6 py-3 text-center text-xs font-bold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-100'}`}>
                                            {roles.map((role) => (
                                                <tr key={role.id} className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-gray-50'} transition-colors`}>
                                                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                        {role.name}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="relative inline-flex items-center gap-1">
                                                            {(["Admin", "Editor", "Viewer"] as const).map((perm) => (
                                                                <button
                                                                    key={perm}
                                                                    onClick={() => handleRolePermissionToggle(role.id, perm)}
                                                                    disabled={updatingRoleId === role.id}
                                                                    className={`px-4 py-1.5 text-xs font-semibold rounded transition-all duration-200 border ${role.permission === perm
                                                                        ? isDark
                                                                            ? 'bg-[#1a1c23] text-white border-[#2E2F33] shadow-sm'
                                                                            : 'bg-[#1a2332] text-white border-[#1a2332]'
                                                                        : isDark
                                                                            ? 'bg-transparent text-gray-400 border-[#2E2F33] hover:border-gray-500'
                                                                            : 'bg-transparent text-gray-500 border-gray-200 hover:border-gray-400'
                                                                        } ${updatingRoleId === role.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                                >
                                                                    {perm}
                                                                </button>
                                                            ))}
                                                            {updatingRoleId === role.id && (
                                                                <div className={`absolute inset-0 flex items-center justify-center rounded ${isDark ? 'bg-[#1A1C1F]/70' : 'bg-white/70'}`}>
                                                                    <Spinner size="xs" color="zs.accent" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="relative inline-flex items-center gap-1">
                                                            {(["Yes", "No"] as const).map((status) => (
                                                                <button
                                                                    key={status}
                                                                    onClick={() => handleRoleApprovalStatusToggle(role.id, status)}
                                                                    disabled={updatingRoleId === role.id}
                                                                    className={`px-4 py-1.5 text-xs font-semibold rounded transition-all duration-200 border ${(role.approvalStatus || "No") === status
                                                                        ? isDark
                                                                            ? status === "Yes"
                                                                                ? 'bg-[#1a2e1a] text-[#68D391] border-[#48BB78] shadow-sm'
                                                                                : 'bg-[#2e1a1a] text-[#FEB2B2] border-[#FC8181] shadow-sm'
                                                                            : status === "Yes"
                                                                                ? 'bg-[#f0fff4] text-[#48BB78] border-[#48BB78]'
                                                                                : 'bg-[#fff5f5] text-[#FC8181] border-[#FC8181]'
                                                                        : isDark
                                                                            ? 'bg-transparent text-gray-400 border-[#2E2F33] hover:border-gray-500'
                                                                            : 'bg-transparent text-gray-500 border-gray-200 hover:border-gray-400'
                                                                        } ${updatingRoleId === role.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                                >
                                                                    {status}
                                                                </button>
                                                            ))}
                                                            {updatingRoleId === role.id && (
                                                                <div className={`absolute inset-0 flex items-center justify-center rounded ${isDark ? 'bg-[#1A1C1F]/70' : 'bg-white/70'}`}>
                                                                    <Spinner size="xs" color="zs.accent" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <button
                                                            onClick={() => handleDeleteRole(role.id)}
                                                            className={`p-2 rounded-md transition-all duration-200 border ${isDark
                                                                ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                                                                : 'border-red-200 text-red-500 hover:bg-red-50'
                                                                }`}
                                                            title="Delete role"
                                                        >
                                                            <IconWrapper icon={FaTrash} size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>


                            </>
                        )}
                    </div>

                    {/* Add New Role Card */}
                    <div className={`rounded-md overflow-hidden ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'bg-white border border-gray-200 shadow-sm'}`}>
                        <div className={`flex items-center justify-between p-5`}>
                            <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Add New Project Role</h4>
                            <div className="flex items-center gap-3">
                                <input
                                    type="text"
                                    placeholder="Enter Role Name"
                                    value={newRoleName}
                                    onChange={(e) => setNewRoleName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") handleAddRoleSubmit(); }}
                                    className={`px-4 py-2.5 text-sm rounded-md border outline-none transition-all duration-200 w-36 ${isDark
                                        ? 'bg-[#0B0B0F] text-white border-[#2E2F33] focus:border-[#B59143]'
                                        : 'bg-white text-gray-900 border-gray-200 focus:border-[#B59143]'
                                        }`}
                                />
                                <CustomDropdown
                                    value={newRolePermission}
                                    onChange={(value) => setNewRolePermission(value)}
                                    isDark={isDark}
                                    placeholder="Permission"
                                    style={{ width: '110px' }}
                                    options={[
                                        { value: "Admin", label: "Admin" },
                                        { value: "Editor", label: "Editor" },
                                        { value: "Viewer", label: "Viewer" },
                                    ]}
                                />
                                <CustomDropdown
                                    value={newRoleApprovalStatus}
                                    onChange={(value) => setNewRoleApprovalStatus(value)}
                                    isDark={isDark}
                                    placeholder="Select Approval Status"
                                    style={{ width: '220px' }}
                                    options={[
                                        { value: "Yes", label: "Yes" },
                                        { value: "No", label: "No" },
                                    ]}
                                />
                                <button
                                    onClick={handleAddRoleSubmit}
                                    disabled={isAddingRole}
                                    className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-xs font-semibold tracking-wider transition-all duration-200 ${isDark
                                        ? 'bg-[#1a2332] text-white hover:bg-[#243044]'
                                        : 'bg-[#1a2332] text-white hover:bg-[#243044]'
                                        } ${isAddingRole ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    {isAddingRole ? <Spinner size="sm" color="inherit" /> : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                        </svg>
                                    )}
                                    Add Role
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Box>
    );
};

export default WorkGroup;
